import React, { useMemo } from "react";
import { Card, CardContent, Typography, Box, Chip, Stack } from "@mui/material";
import { useAppConfig } from "../../../../context/AppConfigContext";
import { money } from "../../../../utils/money";

function humanizeCompositeKey(key = "") {
  if (!key) return "";
  // Try formats like "3T_TALL", "3T_STANDARD"
  const m = key.match(/^(\d+)T/i);
  if (m) {
    const tiers = Number(m[1]);
    const tall = /TALL/i.test(key);
    return `${tiers}-tier ${tall ? "tall" : "standard"}`;
  }
  // Fallback: "three_tier_standard" -> "three tier standard"
  return String(key).replace(/_/g, " ");
}

function prettyPositionLabel(idx, count, rawPosition) {
  // Prefer explicit position from state when available
  if (rawPosition) {
    const p = String(rawPosition).toLowerCase();
    if (p === "single") return "Single";
    if (p === "bottom") return "Bottom";
    if (p === "top") return "Top";
    if (p === "middle") return "Middle";
  }
  // Otherwise derive
  if (count === 1) return "Single";
  if (idx === 0) return "Bottom";
  if (idx === count - 1) return "Top";
  return "Middle";
}

export default function ReviewCakeSummary({
  hasCake,
  comp,
  cakeLine,
  state,
  total,
}) {
  const { config } = useAppConfig();

  const stylesMap = useMemo(() => {
    const styles =
      config?.weddings?.styles ||
      config?.weddings?.cakeStyles ||
      config?.weddings?.catalog?.styles ||
      [];
    const map = {};
    for (const s of styles) {
      const id = s?.id || s?._id || s?.styleId;
      if (id)
        map[id] =
          s?.displayName || s?.name || s?.title || s?.label || String(id);
    }
    return map;
  }, [config]);

  const flavorsMap = useMemo(() => {
    const flavors =
      config?.weddings?.flavors ||
      config?.weddings?.cakeFlavors ||
      config?.menu?.flavors ||
      [];
    const map = {};
    for (const f of flavors) {
      const id = f?.id || f?._id || f?.flavorId;
      if (id) map[id] = f?.displayName || f?.name || f?.title || f?.label || id;
    }
    return map;
  }, [config]);

  if (!hasCake) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent sx={{ p: 2.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Wedding cake
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            No cake added.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const styleId = state?.cake?.styleId;
  const styleName =
    state?.cake?.styleName || // ✅ snapshot from reducer
    stylesMap[styleId] ||
    (styleId ? String(styleId) : "Selected style");
  const compositeKey = state?.cake?.compositeKey || "";
  const sizeLabel =
    comp?.label ||
    comp?.displayName ||
    humanizeCompositeKey(compositeKey) ||
    compositeKey;

  const servings =
    comp?.servings || comp?.estimatedServings || cakeLine?.servings || null;

  const tiers = Array.isArray(state?.cake?.tiers) ? state.cake.tiers : [];

  // Build per-tier display lines, preferring the snapshot first (no guessing)
  const tierFlavorLines = tiers
    .map((t, i) => {
      const label =
        (typeof t?.flavorNameSnapshot === "string" &&
          t.flavorNameSnapshot.trim()) ||
        (t?.flavorId ? flavorsMap[String(t.flavorId)] : "") ||
        (t?.flavorId ? String(t.flavorId) : "—");

      const pos = prettyPositionLabel(i, tiers.length, t?.position);
      return `${pos}: ${label}`;
    })
    .filter(Boolean);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent sx={{ p: 2.25 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Wedding cake
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {money(total)}
          </Typography>
        </Stack>

        {/* Chips row */}
        <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`Style: ${styleName}`} />
          {sizeLabel ? <Chip size="small" label={sizeLabel} /> : null}
          {servings ? (
            <Chip
              size="small"
              label={`~${servings} servings`}
              variant="outlined"
            />
          ) : null}
        </Box>

        {/* Tier flavors */}
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
            Tier flavors
          </Typography>

          {tierFlavorLines.length ? (
            <Box sx={{ display: "grid", gap: 0.25 }}>
              {tierFlavorLines.map((line, idx) => (
                <Typography key={idx} variant="body2" color="text.secondary">
                  {line}
                </Typography>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No flavors selected.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
